/**
 * React Props Registry Helper
 * Provides utilities for accessing the optimized props registry system
 */

// Global cache for combined props data
const propsCache = new Map();

/**
 * Get props for a specific registry ID from the optimized registry system
 * @param {string} registryId - The block's registry ID
 * @returns {Object|null} The combined props object or null if not found
 */
export function getRegistryProps(registryId) {
  // Check cache first
  if (propsCache.has(registryId)) {
    return propsCache.get(registryId);
  }

  // Try to get from the global registry
  if (window.__REACT_PROPS_GET) {
    const props = window.__REACT_PROPS_GET(registryId);
    if (props) {
      propsCache.set(registryId, props);
      return props;
    }
  }

  // Fallback: Look for block-specific JSON script
  const blockScript = document.querySelector(`script[data-props-registry-id="${registryId}"]`);
  if (blockScript) {
    try {
      const blockData = JSON.parse(blockScript.textContent);
      
      // Get global data
      const globalsScript = document.getElementById('react-globals');
      const globals = globalsScript ? JSON.parse(globalsScript.textContent) : {};
      
      // Get context-specific data (product, article, collection)
      const productScript = document.querySelector('script[id^="react-product-"]');
      const articleScript = document.querySelector('script[id^="react-article-"]');
      const collectionScript = document.querySelector('script[id^="react-collection-"]');
      
      const product = productScript ? JSON.parse(productScript.textContent).product : null;
      const article = articleScript ? JSON.parse(articleScript.textContent).article : null;
      const collection = collectionScript ? JSON.parse(collectionScript.textContent).collection : null;
      
      // Combine all data sources
      const combinedProps = {
        registryId: blockData.registryId,
        componentType: blockData.componentType,
        data: {
          ...blockData.data,
          ...globals,
          product,
          article,
          collection
        }
      };
      
      propsCache.set(registryId, combinedProps);
      return combinedProps;
    } catch (error) {
      console.warn(`Failed to parse props for registry ID ${registryId}:`, error);
      return null;
    }
  }
  
  return null;
}

/**
 * React hook for accessing registry props
 * @param {string} registryId - The block's registry ID
 * @returns {Object} The props object with loading state
 */
export function useRegistryProps(registryId) {
  const [props, setProps] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  React.useEffect(() => {
    if (!registryId) {
      setError(new Error('Registry ID is required'));
      setLoading(false);
      return;
    }
    
    try {
      const propsData = getRegistryProps(registryId);
      setProps(propsData);
      setError(propsData ? null : new Error(`No props found for registry ID: ${registryId}`));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [registryId]);
  
  return { props, loading, error };
}

/**
 * Higher-order component that injects registry props
 * @param {React.Component} Component - The component to wrap
 * @param {string} registryId - The registry ID to fetch props for
 * @returns {React.Component} The wrapped component with props
 */
export function withRegistryProps(Component, registryId) {
  return function WrappedComponent(additionalProps) {
    const { props, loading, error } = useRegistryProps(registryId);
    
    if (loading) {
      return <div className="loading-registry-props">Loading...</div>;
    }
    
    if (error) {
      console.warn('Registry props error:', error);
      return <Component {...additionalProps} />;
    }
    
    return <Component {...props?.data} {...additionalProps} />;
  };
}

/**
 * Get props from legacy data-block-props attribute (backward compatibility)
 * @param {string} elementId - The element ID containing the props
 * @returns {Object|null} The props object or null if not found
 */
export function getLegacyProps(elementId) {
  const element = document.getElementById(elementId);
  if (!element) return null;
  
  const propsAttr = element.getAttribute('data-block-props');
  if (!propsAttr) return null;
  
  try {
    return JSON.parse(propsAttr.replace(/&#39;/g, "'"));
  } catch (error) {
    console.warn(`Failed to parse legacy props for element ${elementId}:`, error);
    return null;
  }
}

/**
 * Universal props getter that works with both new and legacy systems
 * @param {string} identifier - Registry ID or element ID
 * @returns {Object|null} The props object or null if not found
 */
export function getProps(identifier) {
  // Try new registry system first
  const registryProps = getRegistryProps(identifier);
  if (registryProps) return registryProps;
  
  // Fallback to legacy system
  return getLegacyProps(identifier);
}

/**
 * Initialize registry system and set up global helpers
 */
export function initializeRegistry() {
  // Set up global getter if not already available
  if (!window.__REACT_PROPS_GET) {
    window.__REACT_PROPS_GET = getRegistryProps;
  }
  
  // Initialize registry from DOM if needed
  if (window.__INIT_ARCTHEME_PROPS) {
    window.__INIT_ARCTHEME_PROPS();
  }
  
  // Clear cache on page navigation (for SPAs)
  window.addEventListener('beforeunload', () => {
    propsCache.clear();
  });
}

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeRegistry);
  } else {
    initializeRegistry();
  }
}